
// Task (DANVAND 2.0.0 vertical slice): the official DANVAND 2.0.0 XML
// exchange adapter - the boundary between PointView's DANVA domain model
// (DanvaDomainData.js's `feature.domainData.danva`) and real, XSD-valid
// DANVAND XML. This file REPLACES the previous, deliberately-provisional
// PointView-internal XML round-trip format (`serializeDanvaExport`/
// `parseDanvaExport`) that lived here before the real, licensed schema was
// available - that internal format served no purpose once real DANVAND XML
// exists (nothing else in the app consumed it; "Importér" was never wired
// up), so it has been removed rather than kept alongside the real exporter.
//
// ============================================================================
// WHAT THE XSD ANALYSIS ESTABLISHED (source: C:\ProdSys\Schema\DANVAND_2.0.0
// + the shared C:\ProdSys\Schema\kerne package it imports - read directly,
// not guessed; every claim below is traceable to a specific file):
// ============================================================================
//
// 1. DOCUMENT STRUCTURE: DANVAND 2.0.0 has NO combining/envelope schema.
//    Each "Samling" (collection) is its OWN independent top-level XML
//    document with its own root element - confirmed by grepping every
//    `<element name="...Samling">` declared directly in DANVAND_2.0.0/*.xsd:
//    AktiveringSamling, KnudeSamling, LedningSamling, OmraadeSamling,
//    ProjektSamling, StraekningSamling - six separate document types, not
//    one. A DANVAND "export" of a network with both nodes and pipes is
//    therefore genuinely TWO XML documents (VAN_KnudeSamling.xsd's
//    <KnudeSamling> and VAN_LedningSamling.xsd's <LedningSamling>), never
//    one file with two roots - see buildDanvandExport() below, which
//    produces up to two documents and (like the SHP exporter's own
//    mixed-geometry ZIP, established earlier in this project) packages them
//    together as one ZIP download when both are present.
//
// 2. NAMESPACES: two targetNamespaces are involved, always used together -
//    urn:danva:van:1.0.0 (DANVAND_2.0.0's own VAN "Vand" module - the
//    <KnudeSamling>/<LedningSamling>/<Knude>/<Ledning> root elements
//    themselves, plus VAN's own type EXTENSIONS such as Ledning's
//    LedningFunktionKode/LedningKategoriKode/MedieKode and Knude's
//    FunktionStruktur - all declared directly in DANVAND_2.0.0/*.xsd) and
//    urn:danva:ker:1.0.0 (the shared "Fælles Kernemodel" package at
//    ../kerne, imported by every VAN schema - the BASE KnudeType/LinieType
//    complexTypes, ReferencesystemStruktur, Geometri, and every field VAN
//    inherits via <extension>/<group ref> from the kerne types). Because
//    every schema here declares elementFormDefault="qualified", a LOCAL
//    element belongs to whichever schema FILE originally declared it, not
//    to whichever type extends/includes it - e.g. Ledning's own <Geometri>
//    is ker_1.0.0-namespaced (declared in kerne/KER_LedningType.xsd's
//    LinieType) even though it's read via <Ledning> (van_1.0.0-namespaced,
//    declared in DANVAND_2.0.0). Getting this wrong per-element was the
//    single easiest way to fail XSD validation - see the per-field
//    namespace tables in mapKnudeElement()/mapLedningElement() below, each
//    entry sourced from the specific file it was read out of.
//    attributeFormDefault="unqualified" everywhere, so every XML ATTRIBUTE
//    (guid, navn, startknudeGuid, ...) is never namespace-prefixed.
//
// 3. IDENTIFICATION: kerne/KER_GuidType.xsd's `guid` simpleType is a strict
//    lowercase-or-uppercase-hex UUID pattern
//    ([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}) -
//    NOT PointView's own `feature.id` shape. Every Knude/Ledning carries a
//    required `guid` XML attribute. See DanvaDomainData.js's
//    generateDanvaGuid() (crypto.randomUUID(), which produces exactly this
//    shape) - generated ONCE at classification time and reused on every
//    export, satisfying the task's stable-identity requirement without a
//    second identity system.
//
// 4. REFERENCESYSTEMSTRUKTUR: both KnudeSamling and LedningSamling require
//    a ker_1.0.0:ReferencesystemStruktur element as their FIRST child - but
//    its own content (a single, optional <Referencesystem> with
//    DatoOprettet/KoordinatsysKode/KotesysKode) is itself optional
//    (minOccurs="0" - see kerne/KER_ReferencesystemStruktur.xsd). PointView
//    has no reliable DANVA coordinate/height-system CODE for its projects
//    (this project's own point clouds carry an empty `projection` string,
//    already confirmed during the earlier SHP `.prj` work) - inventing a
//    KoordinatsysKode would be exactly the fabrication the task forbids, so
//    this adapter emits an empty <ker_1.0.0:ReferencesystemStruktur/> (the
//    required wrapper, present, with no fabricated content) - the same
//    "never invent a CRS" policy already established for SHP's .prj.
//
// 5. GEOMETRY: Geometri is WKT text, constrained only by a very loose regex
//    (kerne/KER_WktType.xsd - "starts with the word Point/LineString/...",
//    nothing else) - the schema itself does not mandate a specific WKT
//    dialect. This adapter uses the standard OGC "POINT Z (x y z)" /
//    "LINESTRING Z (x1 y1 z1, ...)" tagged-Z form (full float precision,
//    "." decimal point, deterministic `String(number)`), which correctly
//    signals 3D to any real downstream WKT reader - a legitimate
//    implementation choice within what the schema allows, not a guess at
//    what the schema requires.
//
// 6. A CONFIRMED SCHEMA DEFECT - Knude's FunktionStruktur: VAN's own
//    KnudeType (DANVAND_2.0.0/Type/VAN_KnudeType.xsd) extends kerne's
//    KnudeType with a REQUIRED <FunktionStruktur> containing a plain
//    <sequence> of 13 different, mutually-exclusive real-world node roles
//    (Magasin, Tank, Pumpested, Retningsregulering, Ventil, Boring,
//    Endepunkt, FysiskOvergang, Kryds, Tapsted, T_stykke, IntetFlow,
//    Tilslutningspunkt), each with NO minOccurs - meaning, read literally,
//    EVERY Knude must simultaneously BE all 13 of these. The file's own
//    commented-out `<!--<choice minOccurs="0" maxOccurs="1">-->` /
//    `<!--</choice>-->` markers bracketing exactly those 13 elements make
//    the intent obvious (pick AT MOST one) - but as actually written, the
//    <choice> is disabled and its contents sit directly in a <sequence>,
//    which is unambiguous, authoritative XSD: all 13 are mandatory. This
//    adapter does NOT fabricate 13 false domain claims (a node is not
//    simultaneously a Tank AND a Ventil AND a Boring...) just to satisfy
//    what is, by its own commented-out intent, very likely a schema
//    authoring bug - so Knude export is honest and complete for every
//    element EXCEPT this one, and XSD validation of a Knude document will
//    report exactly one class of error here (see this project's XSD
//    validation results / the task's final report, section H/K, for the
//    precise, isolated error text this produces).
//
// ============================================================================
// POINTVIEW SEMANTIC MODEL -> DANVAND EXCHANGE MODEL -> XML (task's own
// required separation, kept as three distinct concerns in this file):
// ============================================================================
//   1. mapKnudeElement()/mapLedningElement(): PointView's danva
//      classification (feature.domainData.danva: guid/objectType/
//      attributes[stable schema field name]/relations[named]) -> a plain,
//      generic {ns, tag, attrs, children} element tree, OR a list of
//      concrete, human-readable ISSUES when required schema data is
//      genuinely missing (never invented - task: "eksport/validering skal
//      rapportere den manglende information tydeligt"). All schema
//      knowledge (which fields are required, their element order, which
//      namespace each belongs to) lives ONLY in these two functions.
//   2. renderElement()/renderDocument(): a completely generic, DANVA-
//      agnostic tree-to-XML-text renderer (task: "undgå at lægge
//      modelregler direkte ind i generisk XML-string-building") - it has no
//      idea what a "Knude" or a "Ledning" is, only {ns, tag, attrs,
//      children|text}.
//   3. buildDanvandExport(): orchestration - classifies which features are
//      Knude/Ledning (features classified against any OTHER objectType are
//      reported as "not yet supported by the official exporter", never
//      silently dropped or exported wrong), resolves Ledning's
//      Startknude/Slutknude relations against the ACTUAL registered target
//      Knude (never a bare geometric coincidence - task's own explicit
//      requirement), and only serializes once there are zero blocking
//      issues project-wide.
import {getDanvaData} from "./DanvaDomainData.js";
import {buildExportBasename} from "../export/exportFilename.js";

const NS = {
	van: "urn:danva:van:1.0.0",
	ker: "urn:danva:ker:1.0.0",
};
const PREFIX = {[NS.van]: "van_1.0.0", [NS.ker]: "ker_1.0.0"};

// ── generic, DANVA-agnostic XML tree + renderer ─────────────────────────────

function el(ns, tag, attrs, children){
	return {ns, tag, attrs: attrs || {}, children: (children || []).filter(Boolean)};
}
function textEl(ns, tag, value){
	return {ns, tag, attrs: {}, text: String(value)};
}

function escapeXml(value){
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function renderElement(node, depth){
	const indent = "\t".repeat(depth);
	const qname = `${PREFIX[node.ns]}:${node.tag}`;
	const attrText = Object.entries(node.attrs).map(([k, v]) => ` ${k}="${escapeXml(v)}"`).join("");

	if(node.text !== undefined) return `${indent}<${qname}${attrText}>${escapeXml(node.text)}</${qname}>`;
	if(node.children.length === 0) return `${indent}<${qname}${attrText}/>`;

	const inner = node.children.map((c) => renderElement(c, depth + 1)).join("\n");
	return `${indent}<${qname}${attrText}>\n${inner}\n${indent}</${qname}>`;
}

// `rootTag` is always van_1.0.0-namespaced (KnudeSamling/LedningSamling are
// both declared directly in DANVAND_2.0.0, not kerne) - deterministic:
// identical input always renders identical text (no timestamps/randomness
// inside the renderer itself; the only non-deterministic input, a freshly-
// generated GUID, is generated once at classification time, not per export -
// see DanvaDomainData.generateDanvaGuid()).
function renderDocument(rootTag, children){
	const root = el(NS.van, rootTag, {}, children);
	const nsDecl = ` xmlns:${PREFIX[NS.van]}="${NS.van}" xmlns:${PREFIX[NS.ker]}="${NS.ker}"`;
	const qname = `${PREFIX[NS.van]}:${rootTag}`;
	const inner = root.children.map((c) => renderElement(c, 1)).join("\n");
	return `<?xml version="1.0" encoding="UTF-8"?>\n<${qname}${nsDecl}>\n${inner}\n</${qname}>\n`;
}

function formatNumber(n){
	return String(n);
}
function wktPoint([x, y, z]){
	return `POINT Z (${formatNumber(x)} ${formatNumber(y)} ${formatNumber(z || 0)})`;
}
function wktLineString(coords){
	return `LINESTRING Z (${coords.map(([x, y, z]) => `${formatNumber(x)} ${formatNumber(y)} ${formatNumber(z || 0)}`).join(", ")})`;
}

// `severity: "blocking"` is genuinely missing PointView data - export
// cannot proceed without either inventing it or asking the user to fill it
// in. `severity: "warning"` is a known, reported, non-fabricatable gap that
// does NOT block export (right now: exactly one - see the FunktionStruktur
// finding in mapKnudeElement() below) - the acceptance criteria's own
// wording allows for either outcome ("blokerer ELLER markerer eksporten
// tydeligt"); this is the one place that distinction is made, deliberately
// not per-caller.
function issue(feature, code, message, severity = "blocking"){
	return {featureId: feature.id, featureName: feature.properties.name, code, message, severity};
}

// ── Knude mapping (source: kerne/KER_KnudeType.xsd's KnudeType, extended by
// DANVAND_2.0.0/Type/VAN_KnudeType.xsd's own van_1.0.0:KnudeType) ─────────
//
// Element order below is the schema's own literal declaration order.
// `data.attributes` is keyed by the EXACT DANVAND schema field name (the
// same `attr.name` the generated Danvand 2.0.0 model definition already
// uses - see DanvaModelData.Danvand_2_0_0.generated.js), never a UI label.
export function mapKnudeElement(feature){
	const data = getDanvaData(feature);
	const issues = [];

	if(feature.geometry.type !== "Point") issues.push(issue(feature, "wrong-geometry-type", "Knude kræver Point-geometri."));

	const navn = data.attributes.navn;
	if(!navn) issues.push(issue(feature, "required-attribute-missing", "Navn (navn) mangler - påkrævet XML-attribut på Knude."));

	const datoOprettetGeometri = data.attributes.DatoOprettetGeometri;
	if(!datoOprettetGeometri) issues.push(issue(feature, "required-attribute-missing", "Dato oprettet (geometri) mangler - påkrævet på Knude."));

	// Task section 6 finding (see this file's own header comment) - VAN's
	// KnudeType additionally requires a <FunktionStruktur> this adapter
	// deliberately does not fabricate (would mean asserting a node is
	// simultaneously a Tank AND a Ventil AND a Boring...). Reported as a
	// WARNING, not a blocking issue - the acceptance criteria explicitly
	// allow either "blocks" or "clearly marks" for missing required data,
	// and this is a confirmed, universal, schema-authoring defect (not a
	// per-object PointView data gap - see the disabled <choice> markers this
	// file's header comment quotes), so it is reported on every Knude
	// export rather than silently pretending the resulting document is
	// fully schema-valid.
	const warnings = [issue(feature, "unsupported-required-element",
		"DANVAND 2.0.0 kræver et FunktionStruktur-element pr. Knude (Magasin/Tank/Pumpested/.../Tilslutningspunkt) - " +
		"PointView har ingen reel data for dette, og det udfyldes ikke med opfundne værdier. Se rapportens afsnit om kendte begrænsninger.",
		"warning")];

	if(issues.length > 0) return {issues, warnings};

	const children = [
		data.attributes.DatoDbHistorisk ? textEl(NS.ker, "DatoDbHistorisk", data.attributes.DatoDbHistorisk) : null,
		data.attributes.DatoFejlKonstateret ? textEl(NS.ker, "DatoFejlKonstateret", data.attributes.DatoFejlKonstateret) : null,
		data.attributes.DatoOpdateretGeometri ? textEl(NS.ker, "DatoOpdateretGeometri", data.attributes.DatoOpdateretGeometri) : null,
		textEl(NS.ker, "DatoOprettetGeometri", datoOprettetGeometri),
		textEl(NS.ker, "Geometri", wktPoint(feature.geometry.coordinates)),
		data.attributes.InitialerGeometri ? textEl(NS.ker, "InitialerGeometri", data.attributes.InitialerGeometri) : null,
		data.attributes.Terraenkote != null ? textEl(NS.ker, "Terraenkote", formatNumber(data.attributes.Terraenkote)) : null,
	];

	return {issues: [], warnings, element: el(NS.van, "Knude", {guid: data.guid, navn}, children)};
}

// Resolves a named relation (e.g. "Startknude") on `feature` to its target
// Knude's own real, already-classified guid/navn - NEVER a geometric
// coincidence (task's own explicit requirement: PointView's topology/
// snapping may SUGGEST a relation, DanvaRelations.suggestRelationsFromTopology,
// but only an actually-recorded semantic relation is ever used here).
function resolveKnudeReference(feature, featureManager, relationName){
	const data = getDanvaData(feature);
	const relation = data.relations.find((r) => r.name === relationName);
	if(!relation){
		return {issue: issue(feature, "required-relation-missing", `Obligatorisk relation "${relationName}" mangler.`)};
	}
	const target = featureManager.get(relation.targetFeatureId);
	if(!target){
		return {issue: issue(feature, "dangling-relation", `Relationen "${relationName}" peger på et objekt der ikke længere findes.`)};
	}
	const targetData = getDanvaData(target);
	if(!targetData || targetData.objectType !== "Knude"){
		return {issue: issue(feature, "relation-target-not-knude", `Relationen "${relationName}" peger på et objekt der ikke er registreret som Knude.`)};
	}
	const navn = targetData.attributes.navn;
	if(!navn){
		return {issue: issue(feature, "relation-target-incomplete", `Relationen "${relationName}"s mål (Knude "${target.properties.name}") mangler selv sit påkrævede Navn.`)};
	}
	return {guid: targetData.guid, navn};
}

// ── Ledning mapping (source: kerne/KER_LedningType.xsd's LinieType
// (abstract) + LedningGruppe group, extended by
// DANVAND_2.0.0/Type/VAN_LedningType.xsd's own van_1.0.0:LedningType) ─────
export function mapLedningElement(feature, featureManager){
	const data = getDanvaData(feature);
	const issues = [];

	if(feature.geometry.type !== "LineString") issues.push(issue(feature, "wrong-geometry-type", "Ledning kræver LineString-geometri."));

	const start = resolveKnudeReference(feature, featureManager, "Startknude");
	if(start.issue) issues.push(start.issue);
	const slut = resolveKnudeReference(feature, featureManager, "Slutknude");
	if(slut.issue) issues.push(slut.issue);

	const datoOprettet = data.attributes.DatoOprettet;
	if(!datoOprettet) issues.push(issue(feature, "required-attribute-missing", "Dato oprettet mangler - påkrævet på Ledning."));

	if(issues.length > 0) return {issues, warnings: []};

	const children = [
		// LinieType's own base sequence (ker_1.0.0)
		data.attributes.DatoDBhistorisk ? textEl(NS.ker, "DatoDBhistorisk", data.attributes.DatoDBhistorisk) : null,
		data.attributes.DatoFejlKonstateret ? textEl(NS.ker, "DatoFejlKonstateret", data.attributes.DatoFejlKonstateret) : null,
		data.attributes.DatoOpdateretGeometri ? textEl(NS.ker, "DatoOpdateretGeometri", data.attributes.DatoOpdateretGeometri) : null,
		data.attributes.DatoOprettetGeometri ? textEl(NS.ker, "DatoOprettetGeometri", data.attributes.DatoOprettetGeometri) : null,
		textEl(NS.ker, "Geometri", wktLineString(feature.geometry.coordinates)),
		data.attributes.InitialerGeometri ? textEl(NS.ker, "InitialerGeometri", data.attributes.InitialerGeometri) : null,
		data.attributes.Loebenr != null ? textEl(NS.ker, "Loebenr", formatNumber(data.attributes.Loebenr)) : null,
		// LedningGruppe (ker_1.0.0, via <group ref>)
		data.attributes.Bemaerkning ? textEl(NS.ker, "Bemaerkning", data.attributes.Bemaerkning) : null,
		data.attributes.DatoEtableret ? textEl(NS.ker, "DatoEtableret", data.attributes.DatoEtableret) : null,
		data.attributes.DatoOpdateret ? textEl(NS.ker, "DatoOpdateret", data.attributes.DatoOpdateret) : null,
		textEl(NS.ker, "DatoOprettet", datoOprettet),
		data.attributes.DatoStatus ? textEl(NS.ker, "DatoStatus", data.attributes.DatoStatus) : null,
		data.attributes.Initialer ? textEl(NS.ker, "Initialer", data.attributes.Initialer) : null,
		data.attributes.LaegningsmetodeKode ? textEl(NS.ker, "LaegningsmetodeKode", data.attributes.LaegningsmetodeKode) : null,
		data.attributes.LedningTransportKode ? textEl(NS.ker, "LedningTransportKode", data.attributes.LedningTransportKode) : null,
		data.attributes.Netberegning != null ? textEl(NS.ker, "Netberegning", data.attributes.Netberegning) : null,
		data.attributes.NyTilslutningKode ? textEl(NS.ker, "NyTilslutningKode", data.attributes.NyTilslutningKode) : null,
		data.attributes.StatusKode ? textEl(NS.ker, "StatusKode", data.attributes.StatusKode) : null,
		// VAN LedningType's own extension (van_1.0.0)
		data.attributes.LedningFunktionKode ? textEl(NS.van, "LedningFunktionKode", data.attributes.LedningFunktionKode) : null,
		data.attributes.LedningKategoriKode ? textEl(NS.van, "LedningKategoriKode", data.attributes.LedningKategoriKode) : null,
		data.attributes.MedieKode ? textEl(NS.van, "MedieKode", data.attributes.MedieKode) : null,
		data.attributes.ForbrugPaaLedning != null ? textEl(NS.van, "ForbrugPaaLedning", data.attributes.ForbrugPaaLedning) : null,
	];

	const attrs = {
		guid: data.guid,
		startknudeGuid: start.guid,
		startknudenavn: start.navn,
		slutknudeGuid: slut.guid,
		slutknudenavn: slut.navn,
	};
	return {issues: [], warnings: [], element: el(NS.van, "Ledning", attrs, children)};
}

// ── orchestration ────────────────────────────────────────────────────────

// Pure - no XML built, no packaging. Used both by buildDanvandExport() below
// and directly by the DANVA panel's own "is this project ready for official
// export" status display, so the two can never disagree (task: "se om
// projektets DANVA-data er klar til officiel eksport" / "se konkrete
// valideringsfejl før eksport").
export function validateForDanvandExport(features, featureManager){
	const issues = [];
	const warnings = [];
	let knudeCount = 0, ledningCount = 0;

	for(const feature of features){
		const data = getDanvaData(feature);
		if(!data) continue;

		if(data.objectType === "Knude"){
			knudeCount++;
			const result = mapKnudeElement(feature);
			issues.push(...result.issues);
			warnings.push(...(result.warnings || []));
		}else if(data.objectType === "Ledning"){
			ledningCount++;
			const result = mapLedningElement(feature, featureManager);
			issues.push(...result.issues);
			warnings.push(...(result.warnings || []));
		}else{
			issues.push(issue(feature, "unsupported-object-type",
				`Objekttypen "${data.objectType}" understøttes endnu ikke af den officielle DANVAND-eksport (kun Knude og Ledning i denne version).`));
		}
	}

	return {ready: issues.length === 0 && (knudeCount > 0 || ledningCount > 0), issues, warnings, knudeCount, ledningCount};
}

// Builds the real DANVAND 2.0.0 document(s). Returns {ready:false, issues}
// unchanged from validateForDanvandExport() if anything blocks export -
// never a partial/best-effort XML. On success, returns one entry per
// non-empty Samling type, filename built from the SAME shared
// exportFilename.js convention every other general PointView export format
// uses (task: "Filnavngivningen skal om muligt være fælles").
export function buildDanvandExport(features, featureManager, context = {}){
	const result = validateForDanvandExport(features, featureManager);
	if(!result.ready) return result;

	const basename = buildExportBasename(context.pointcloudName, context.exportDate);
	const documents = [];

	if(result.knudeCount > 0){
		const knudeElements = features
			.filter((f) => getDanvaData(f) && getDanvaData(f).objectType === "Knude")
			.map((f) => mapKnudeElement(f).element);
		documents.push({
			filename: `${basename}_KnudeSamling.xml`,
			text: renderDocument("KnudeSamling", [el(NS.ker, "ReferencesystemStruktur", {}, [])].concat(knudeElements)),
		});
	}

	if(result.ledningCount > 0){
		const ledningElements = features
			.filter((f) => getDanvaData(f) && getDanvaData(f).objectType === "Ledning")
			.map((f) => mapLedningElement(f, featureManager).element);
		documents.push({
			filename: `${basename}_LedningSamling.xml`,
			text: renderDocument("LedningSamling", [el(NS.ker, "ReferencesystemStruktur", {}, [])].concat(ledningElements)),
		});
	}

	return {...result, documents};
}
